import React, { useRef, useState } from "react";
import { FaWhatsapp, FaInstagram, FaFacebook, FaPhone, FaBox, FaMapMarkerAlt, FaClock, FaPrint, FaDownload } from "react-icons/fa";
import Headercomponent from "./Headercomponent";
import { Link, useLocation } from "react-router-dom";
import html2canvas from "html2canvas";

const ThankYouPage = () => {
  const location = useLocation();
  const receiptRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const {
    orderId,
    transactionRef, // This should come from checkout
    customerName,
    totalAmount,
    deliveryAddress,
    deliveryFee,
    subtotal,
    estimatedDelivery,
    cartItems = [],
    orderTimestamp = new Date().toISOString() // Default to current time if not provided
  } = location.state || {};

  // Use transactionRef as order number if available
  const orderNumber = transactionRef || orderId;
  
  // Create a fixed date object from the order timestamp
  const orderDate = new Date(orderTimestamp);

  const downloadReceiptAsImage = async () => {
    if (!receiptRef.current) return;
    
    setIsDownloading(true);
    try {
      // First, make the receipt element full width to capture everything
      const originalStyles = {
        width: receiptRef.current.style.width,
        maxWidth: receiptRef.current.style.maxWidth,
        overflow: receiptRef.current.style.overflow,
      };
      
      // Temporarily expand the receipt for full capture
      receiptRef.current.style.width = '100%';
      receiptRef.current.style.maxWidth = 'none';
      receiptRef.current.style.overflow = 'visible';
      
      // Wait for the DOM to update
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        width: receiptRef.current.scrollWidth,
        height: receiptRef.current.scrollHeight,
        onclone: (clonedDoc) => {
          // Ensure all content is visible in the clone
          const receipt = clonedDoc.querySelector('[ref="receiptRef"]');
          if (receipt) {
            receipt.style.width = '100%';
            receipt.style.maxWidth = 'none';
            receipt.style.overflow = 'visible';
            
            // Make sure all text is visible
            const itemCells = clonedDoc.querySelectorAll('.item-name');
            itemCells.forEach(el => {
              el.style.whiteSpace = 'normal';
              el.style.wordBreak = 'break-word';
            });

            // Ensure long reference numbers are fully visible
            const orderNumbers = clonedDoc.querySelectorAll('.order-number');
            orderNumbers.forEach(el => {
              el.style.whiteSpace = 'normal';
              el.style.wordBreak = 'break-all';
              el.style.fontSize = '0.9em';
            });
          }
        }
      });

      // Restore original styles
      receiptRef.current.style.width = originalStyles.width;
      receiptRef.current.style.maxWidth = originalStyles.maxWidth;
      receiptRef.current.style.overflow = originalStyles.overflow;

      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      link.download = `Lihen-Receipt-${orderNumber || timestamp}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      alert('Could not generate receipt image. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const formatDeliveryTime = (dateString) => {
    if (!dateString) return "Calculating...";
    
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      
      if (isNaN(date.getTime())) {
        return "Today within 1 hour";
      }

      const options = {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };

      return date.toLocaleString('en-US', options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Today within 1 hour";
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (!location.state) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <h1 className="text-3xl font-bold text-red-600 mb-4">Order Not Found</h1>
          <p className="text-gray-600 mb-6">
            We couldn't retrieve your order details. Please check your email for confirmation or contact support.
          </p>
          <div className="flex flex-col space-y-4">
            <Link to="/checkout" className="w-full">
              <button className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 transition duration-300">
                Go to Checkout
              </button>
            </Link>
            <Link to="/drugs" className="w-full">
              <button className="w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-medium hover:bg-gray-300 transition duration-300">
                Continue Shopping
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Headercomponent />
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        {/* Loading overlay */}
        {isDownloading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
              <p className="text-lg font-medium">Generating Image...</p>
              <p className="text-sm text-gray-600 mt-1">Please wait while we prepare your download</p>
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto">
          <div className="bg-white shadow-xl rounded-lg overflow-hidden mb-8">
            <div className="bg-green-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-8 h-8 text-white mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <h1 className="text-2xl font-bold text-white">Order Confirmed</h1>
                </div>
                <span className="bg-white text-green-600 text-sm font-medium px-3 py-1 rounded-full order-number" 
                      style={{maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                  #{orderNumber || 'ORDER123'}
                </span>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <p className="text-gray-700 mb-4">
                  Thank you, <span className="font-semibold">{customerName || 'Customer'}</span>! Your order has been confirmed.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 border border-green-100 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FaBox className="text-green-600 mr-2" />
                      <h3 className="font-medium text-gray-800">Order Prepared</h3>
                    </div>
                    <p className="text-sm text-gray-600">Items are being prepared for delivery</p>
                  </div>
                  
                  <div className="bg-green-50 border border-green-100 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FaMapMarkerAlt className="text-green-600 mr-2" />
                      <h3 className="font-medium text-gray-800">Delivery Address</h3>
                    </div>
                    <p className="text-sm text-gray-600 break-words">{deliveryAddress || 'Address not specified'}</p>
                  </div>
                  
                  <div className="bg-green-50 border border-green-100 p-4 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FaClock className="text-green-600 mr-2" />
                      <h3 className="font-medium text-gray-800">Estimated Delivery</h3>
                    </div>
                    <p className="text-sm text-gray-600">{formatDeliveryTime(estimatedDelivery)}</p>
                  </div>
                </div>

                <div 
                  ref={receiptRef}
                  className="border border-gray-200 rounded-lg overflow-hidden mb-4 bg-white"
                  style={{
                    width: '100%',
                    maxWidth: '400px',
                    margin: '0 auto',
                    overflow: 'visible'
                  }}
                >
                  <div className="bg-white p-4 text-center border-b border-gray-200">
                    <h2 className="text-lg font-bold text-green-600">LIHEN PHARMACY</h2>
                    <div className="border-t border-gray-200 my-2"></div>
                    <p className="text-sm font-medium order-number break-all px-2">
                      Order #{orderNumber || 'ORDER123'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {/* Use the fixed order date instead of current time */}
                      {orderDate.toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  
                  <div className="p-3 border-b border-gray-200 bg-gray-50">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="font-medium">Customer:</span>
                        <p className="break-words">{customerName || 'Walk-in Customer'}</p>
                      </div>
                      <div>
                        <span className="font-medium">Delivery:</span>
                        <p className="break-words">{deliveryAddress || 'Store Pickup'}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="divide-y divide-gray-200">
                    <div className="grid grid-cols-12 p-2 bg-gray-50 text-xs font-medium border-b border-gray-200">
                      <div className="col-span-5 text-center">ITEM</div>
                      <div className="col-span-2 text-center">QTY</div>
                      <div className="col-span-2 text-center">PRICE</div>
                      <div className="col-span-3 text-center">TOTAL</div>
                    </div>
                    {cartItems.length > 0 ? (
                      cartItems.map((item, index) => (
                        <div key={index} className="grid grid-cols-12 p-2 text-sm">
                          <div className="col-span-5 text-center break-words px-1 item-name">
                            {item.productName || 'Product'}
                          </div>
                          <div className="col-span-2 text-center">
                            {item.quantity || 1}
                          </div>
                          <div className="col-span-2 text-center">
                            ₦{(item.amount / (item.quantity || 1)).toLocaleString('en-US')}
                          </div>
                          <div className="col-span-3 text-center">
                            ₦{(item.amount || 0).toLocaleString('en-US')}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        No items in this order
                      </div>
                    )}
                  </div>
                  
                  <div className="p-3 border-t border-gray-200 bg-gray-50">
                    <div className="flex justify-between text-sm mb-1">
                      <span>Subtotal:</span>
                      <span>₦{subtotal?.toLocaleString('en-US') || '0.00'}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Delivery Fee:</span>
                      <span>₦{deliveryFee?.toLocaleString('en-US') || '0.00'}</span>
                    </div>
                    <div className="flex justify-between font-bold mt-2 pt-2 border-t border-gray-300 text-green-600">
                      <span>TOTAL:</span>
                      <span>₦{totalAmount?.toLocaleString('en-US') || '0.00'}</span>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-gray-50 text-center text-xs border-t border-gray-200">
                    <p>Thank you for your patronage!</p>
                    <p className="mt-1"> For Returns Please Check our Refund Policy</p>
                    <p className="mt-1">www.lihenpharmacy.com</p>
                  </div>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row justify-center gap-4">
                  <button 
                    onClick={downloadReceiptAsImage}
                    disabled={isDownloading}
                    className={`flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition duration-300 shadow-md ${
                      isDownloading ? 'opacity-75 cursor-not-allowed' : ''
                    }`}
                  >
                    <FaDownload className="mr-2" />
                    {isDownloading ? 'Generating Image...' : 'Download Receipt'}
                  </button>
                  <button 
                    onClick={handlePrint}
                    className="flex items-center justify-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition duration-300 shadow-md"
                  >
                    <FaPrint className="mr-2" />
                    Print Receipt
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                <h3 className="font-medium text-blue-800 mb-2">Need help with your order?</h3>
                <div className="flex flex-wrap items-center justify-center gap-4 mt-3">
                  <a href="tel:09139281851" className="flex items-center text-blue-600 hover:text-blue-800">
                    <FaPhone className="mr-2" />
                    <span>09139281851</span>
                  </a>
                  <a href="https://wa.me/+2349139281851" target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800">
                    <FaWhatsapp className="mr-2" />
                    <span>WhatsApp</span>
                  </a>
                  <a href="https://instagram.com/Lihenpharmacy" target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800">
                    <FaInstagram className="mr-2" />
                    <span>Instagram</span>
                  </a>
                  <a href="https://www.facebook.com/share/19obB3BAyq/" target="_blank" rel="noopener noreferrer" className="flex items-center text-blue-600 hover:text-blue-800">
                    <FaFacebook className="mr-2" />
                    <span>Facebook</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-center">
                <Link to="/drugs">
                  <button className="mb-2 sm:mb-0 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition duration-300">
                    Continue Shopping
                  </button>
                </Link>
                <p className="text-xs text-gray-500 mt-4 sm:mt-0 order-number break-all">
                  Order #{orderNumber || 'ORDER123'} • {orderDate.toLocaleDateString('en-US')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ThankYouPage;